/**
 * Sprint 207S — Rebecca Manager admin tile (two tabs).
 *
 *   • Identity tab — admin edits Rebecca's first name, portrait URL, voice
 *     slot. Optional "Apply to all existing teens" toggle cascades the
 *     change across every Rebecca AvA in MongoDB in one shot. Portrait URL
 *     runs through the OpenAI Vision celebrity gate before save.
 *
 *   • Scripted Conversations tab — admin authors canned messages Rebecca
 *     delivers verbatim. Each script has a trigger (`first_session`,
 *     `second_session`, `lapse_day_2`, `post_challenge_complete`,
 *     `on_demand`) and an `order_index` so multiple messages can fire
 *     in sequence for the same trigger. Active toggle, edit + delete.
 */
import React, { useEffect, useState } from 'react';
import {
  User as UserIcon, MessageSquare, Plus, Trash2, Pencil, Save, Loader2,
  AlertCircle, ToggleLeft, ToggleRight, X, RefreshCcw, PlayCircle, Volume2,
  ChevronDown, ChevronRight,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import RebeccaPreviewModal from './MayaPreviewModal';

const TRIGGER_LABELS = {
  first_session: 'First session',
  second_session: 'Second session',
  lapse_day_2: 'Day-2 silent (lapse)',
  topic_start_math: 'Topic start · Math',
  topic_start_science: 'Topic start · Science',
  topic_start_coding: 'Topic start · Coding',
  on_demand: 'On demand (manual)',
};

const TEEN_VOICE_OPTIONS = [
  ...['tf1','tf2','tf3','tf4','tf5','tf6','tf7','tf8','tf9','tf10'].map((v, i) => ({ value: v, label: `Female Voice ${i+1}` })),
  ...['tm1','tm2','tm3','tm4','tm5','tm6','tm7','tm8','tm9','tm10'].map((v, i) => ({ value: v, label: `Male Voice ${i+1}` })),
];

// Same client-side voice modulation map as avaLibrary/data.js → TEEN_VOICES
const VOICE_MODIFIERS = {
  tf1:  { baseVoice: 'nova',    pitch: 1.18, speed: 1.05 },
  tf2:  { baseVoice: 'shimmer', pitch: 1.22, speed: 1.00 },
  tf3:  { baseVoice: 'nova',    pitch: 1.05, speed: 1.08 },
  tf4:  { baseVoice: 'shimmer', pitch: 1.10, speed: 1.12 },
  tf5:  { baseVoice: 'coral',   pitch: 1.15, speed: 1.00 },
  tf6:  { baseVoice: 'nova',    pitch: 0.95, speed: 1.05 },
  tf7:  { baseVoice: 'fable',   pitch: 1.20, speed: 1.08 },
  tf8:  { baseVoice: 'shimmer', pitch: 0.98, speed: 1.03 },
  tf9:  { baseVoice: 'coral',   pitch: 1.08, speed: 1.10 },
  tf10: { baseVoice: 'alloy',   pitch: 1.12, speed: 1.00 },
  tm1:  { baseVoice: 'echo',    pitch: 1.05, speed: 1.05 },
  tm2:  { baseVoice: 'onyx',    pitch: 1.18, speed: 1.00 },
  tm3:  { baseVoice: 'echo',    pitch: 1.12, speed: 1.08 },
  tm4:  { baseVoice: 'ash',     pitch: 1.15, speed: 1.02 },
  tm5:  { baseVoice: 'onyx',    pitch: 1.08, speed: 1.05 },
  tm6:  { baseVoice: 'echo',    pitch: 0.98, speed: 1.10 },
  tm7:  { baseVoice: 'sage',    pitch: 1.10, speed: 1.05 },
  tm8:  { baseVoice: 'ash',     pitch: 1.05, speed: 1.10 },
  tm9:  { baseVoice: 'fable',   pitch: 1.02, speed: 1.05 },
  tm10: { baseVoice: 'onyx',    pitch: 1.22, speed: 1.05 },
};

const RebeccaManagerTile = () => {
  const [tab, setTab] = useState('identity');

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="rebecca-manager-tile">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 shrink-0">
            <UserIcon size={18} className="text-white" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 leading-tight">Rebecca · Science & Technology Tutor</h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-snug">Identity · Scripted opening messages · Generic teen AvA templates</p>
          </div>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-full sm:w-auto overflow-x-auto">
          <button
            onClick={() => setTab('identity')}
            className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${tab === 'identity' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="rebecca-manager-tab-identity"
          >Identity</button>
          <button
            onClick={() => setTab('scripts')}
            className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${tab === 'scripts' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="rebecca-manager-tab-scripts"
          >Scripts</button>
          <button
            onClick={() => setTab('templates')}
            className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${tab === 'templates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            data-testid="rebecca-manager-tab-templates"
          >Templates</button>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {tab === 'identity' && <IdentityPanel />}
        {tab === 'scripts' && <ScriptsPanel />}
        {tab === 'templates' && <TemplatesPanel />}
      </div>
    </div>
  );
};

// ── Identity panel ─────────────────────────────────────────────────────

const IdentityPanel = () => {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [applyToAll, setApplyToAll] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState(null);
  // Sprint 207V — voice columns collapsed by default to save vertical space.
  // Auto-expanded if the currently-saved slot lives in that column.
  const [voiceColumnsOpen, setVoiceColumnsOpen] = useState({ female: false, male: false });
  const previewAudioRef = React.useRef(null);

  const playVoiceSample = async (voiceId) => {
    if (previewAudioRef.current) {
      try { previewAudioRef.current.pause(); } catch {}
      previewAudioRef.current = null;
    }
    if (previewingVoice === voiceId) { setPreviewingVoice(null); return; }
    setPreviewingVoice(voiceId);
    const mods = VOICE_MODIFIERS[voiceId] || { baseVoice: 'nova', pitch: 1.0, speed: 1.0 };
    try {
      const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${apiUrl}/api/tts/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ voice: mods.baseVoice, language: 'en', speed: mods.speed }),
      });
      const data = await resp.json();
      if (data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio}`);
        audio.playbackRate = Math.max(0.5, Math.min(2.0, mods.pitch));
        previewAudioRef.current = audio;
        audio.onended = () => setPreviewingVoice(null);
        audio.onerror = () => setPreviewingVoice(null);
        await audio.play();
      } else {
        setPreviewingVoice(null);
      }
    } catch (_e) {
      setPreviewingVoice(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await apiClient.request('/api/rebecca-manager/identity');
        setIdentity(data);
      } catch (e) {
        setError(e?.message || 'Failed to load Rebecca identity');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setError(null); setSuccess(null); setSaving(true);
    try {
      const payload = {
        first_name: identity.first_name,
        portrait_url: identity.portrait_url,
        voice_slot: identity.voice_slot,
        instruction_template: identity.instruction_template,
        apply_to_existing: applyToAll,
      };
      const res = await apiClient.request('/api/rebecca-manager/identity', {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      setIdentity(res.identity);
      setSuccess(
        applyToAll
          ? `Saved. Cascaded to ${res.cascade_count} existing teen${res.cascade_count === 1 ? '' : 's'}.`
          : 'Saved. Future teens will use the new template.'
      );
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  if (!identity) return <div className="text-sm text-red-600">{error || 'Unable to load identity'}</div>;

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-start">
        <img
          src={identity.portrait_url}
          alt="Rebecca"
          className="w-24 h-24 rounded-xl object-cover bg-gray-100 border border-gray-200"
          data-testid="rebecca-identity-portrait-preview"
          onError={(e) => { e.currentTarget.style.opacity = '0.4'; }}
        />
        <div className="flex-1 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">First name</label>
            <input
              type="text"
              value={identity.first_name || ''}
              onChange={(e) => setIdentity({ ...identity, first_name: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              data-testid="rebecca-identity-first-name"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Portrait URL (scanned by celebrity gate on save)</label>
            <input
              type="text"
              value={identity.portrait_url || ''}
              onChange={(e) => setIdentity({ ...identity, portrait_url: e.target.value })}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/40"
              data-testid="rebecca-identity-portrait-url"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Voice slot — click ▶ to hear a sample</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="rebecca-identity-voice-picker">
              {[
                { gender: 'Female', key: 'female', prefix: 'tf', headerBg: 'bg-pink-50',  headerFg: 'text-pink-700',  list: TEEN_VOICE_OPTIONS.filter(o => o.value.startsWith('tf')) },
                { gender: 'Male',   key: 'male',   prefix: 'tm', headerBg: 'bg-blue-50',  headerFg: 'text-blue-700',  list: TEEN_VOICE_OPTIONS.filter(o => o.value.startsWith('tm')) },
              ].map(({ gender, key, prefix, headerBg, headerFg, list }) => {
                const isOpen = voiceColumnsOpen[key];
                const selectedHere = identity.voice_slot && identity.voice_slot.startsWith(prefix);
                const selectedOption = list.find(o => o.value === identity.voice_slot);
                return (
                  <div
                    key={gender}
                    className="border border-gray-200 rounded-md bg-white overflow-hidden"
                    data-testid={`maya-identity-voice-column-${key}`}
                  >
                    <button
                      type="button"
                      onClick={() => setVoiceColumnsOpen(prev => ({ ...prev, [key]: !prev[key] }))}
                      className={`w-full flex items-center justify-between px-3 py-2 border-b border-gray-100 ${headerBg} hover:brightness-95 transition`}
                      data-testid={`maya-identity-voice-toggle-${key}`}
                    >
                      <span className={`flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide ${headerFg}`}>
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {gender} Voices
                        <span className="text-[10px] font-semibold text-gray-700 normal-case tracking-normal">
                          ({list.length} slots
                          {selectedHere && selectedOption ? ` · ${selectedOption.label} selected` : ''})
                        </span>
                      </span>
                      <span className="text-[10px] font-semibold text-gray-700 normal-case tracking-normal">
                        {isOpen ? 'collapse' : 'expand'}
                      </span>
                    </button>

                    {/* Collapsed: show ONLY the selected row inline so admin sees current choice at a glance. */}
                    {!isOpen && selectedHere && selectedOption && (
                      <div className="flex items-center justify-between px-3 py-2 bg-purple-50" data-testid={`maya-identity-voice-collapsed-selection-${key}`}>
                        <span className="flex items-center gap-2 flex-wrap">
                          <span className="inline-block w-3 h-3 rounded-full bg-purple-600 border-2 border-purple-600" />
                          <span className="text-sm font-bold text-purple-900">{selectedOption.label}</span>
                          <span className="text-xs text-gray-500">({selectedOption.value})</span>
                          {VOICE_MODIFIERS[selectedOption.value] && (
                            <span
                              className="ml-1 text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
                              data-testid={`maya-identity-voice-fingerprint-${selectedOption.value}`}
                              title="OpenAI base voice · pitch multiplier · playback speed"
                            >
                              {VOICE_MODIFIERS[selectedOption.value].baseVoice}
                              {' · pitch '}{VOICE_MODIFIERS[selectedOption.value].pitch.toFixed(2)}
                              {' · '}{VOICE_MODIFIERS[selectedOption.value].speed.toFixed(2)}x
                            </span>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); playVoiceSample(selectedOption.value); }}
                          className={`p-1.5 rounded-md transition-colors ${previewingVoice === selectedOption.value ? 'bg-purple-600 text-white animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'}`}
                          title={previewingVoice === selectedOption.value ? 'Stop' : 'Play sample'}
                        >
                          {previewingVoice === selectedOption.value ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
                        </button>
                      </div>
                    )}

                    {isOpen && list.map((o) => {
                      const isSelected = identity.voice_slot === o.value;
                      const isPreviewing = previewingVoice === o.value;
                      const mods = VOICE_MODIFIERS[o.value];
                      return (
                        <div
                          key={o.value}
                          className={`flex items-center justify-between px-3 py-2 border-b border-gray-100 last:border-b-0 ${isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                        >
                          <button
                            type="button"
                            onClick={() => setIdentity({ ...identity, voice_slot: o.value })}
                            className="flex-1 text-left flex items-center gap-2 flex-wrap"
                            data-testid={`maya-identity-voice-${o.value}`}
                          >
                            <span
                              className={`inline-block w-3 h-3 rounded-full border-2 ${isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}
                            />
                            <span className={`text-sm ${isSelected ? 'font-bold text-purple-900' : 'text-gray-800'}`}>{o.label}</span>
                            <span className="text-xs text-gray-500">({o.value})</span>
                            {mods && (
                              <span
                                className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700"
                                data-testid={`maya-identity-voice-fingerprint-${o.value}`}
                                title="OpenAI base voice · pitch multiplier · playback speed"
                              >
                                {mods.baseVoice}
                                {' · pitch '}{mods.pitch.toFixed(2)}
                                {' · '}{mods.speed.toFixed(2)}x
                              </span>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => playVoiceSample(o.value)}
                            className={`p-1.5 rounded-md transition-colors ${isPreviewing ? 'bg-purple-600 text-white animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-purple-100 hover:text-purple-700'}`}
                            title={isPreviewing ? 'Stop' : 'Play sample'}
                            data-testid={`maya-identity-voice-preview-${o.value}`}
                          >
                            {isPreviewing ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
          Instruction Template (STEM persona)
        </label>
        <textarea
          value={identity.instruction_template || ''}
          onChange={(e) => setIdentity({ ...identity, instruction_template: e.target.value })}
          rows={6}
          maxLength={4000}
          placeholder="Rebecca's Socratic STEM persona…"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 leading-relaxed"
          data-testid="rebecca-identity-instruction-template"
        />
        <p className="mt-1 text-[11px] text-gray-500">
          Prepended to every reply Rebecca sends. NeMo Guardrails still fire independently — off-topic drift is intercepted regardless of what this prompt says.
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid="rebecca-identity-apply-toggle">
        <input
          type="checkbox"
          checked={applyToAll}
          onChange={(e) => setApplyToAll(e.target.checked)}
          className="rounded text-purple-600 focus:ring-purple-500"
        />
        <span>Apply changes to every existing teen&apos;s Rebecca immediately (cascade)</span>
      </label>

      {error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle size={14} /> {error}</div>}
      {success && <div className="flex items-center gap-2 text-sm text-green-600"><Save size={14} /> {success}</div>}

      <button
        onClick={handleSave}
        disabled={saving}
        className="w-full sm:w-auto px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="rebecca-identity-save-btn"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        Save identity
      </button>
    </div>
  );
};

// ── Scripts panel ──────────────────────────────────────────────────────

const ScriptsPanel = () => {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/rebecca-manager/scripts');
      setScripts(data.rows || []);
    } catch (e) {
      setError(e?.message || 'Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => {
    setEditingId('new');
    setDraft({
      title: '',
      trigger_event: 'first_session',
      message: '',
      order_index: scripts.filter(s => s.trigger_event === 'first_session').length,
      active: true,
    });
  };

  const startEdit = (s) => {
    setEditingId(s.script_id);
    setDraft({ ...s });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveDraft = async () => {
    setError(null);
    try {
      if (editingId === 'new') {
        await apiClient.request('/api/rebecca-manager/scripts', {
          method: 'POST',
          body: JSON.stringify({
            title: draft.title,
            trigger_event: draft.trigger_event,
            message: draft.message,
            order_index: Number(draft.order_index) || 0,
            active: draft.active !== false,
          }),
        });
      } else {
        await apiClient.request(`/api/rebecca-manager/scripts/${editingId}`, {
          method: 'PATCH',
          body: JSON.stringify({
            title: draft.title,
            trigger_event: draft.trigger_event,
            message: draft.message,
            order_index: Number(draft.order_index) || 0,
            active: draft.active !== false,
          }),
        });
      }
      cancelEdit();
      await reload();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const removeScript = async (script_id) => {
    if (!window.confirm('Delete this Rebecca script?')) return;
    try {
      await apiClient.request(`/api/rebecca-manager/scripts/${script_id}`, { method: 'DELETE' });
      await reload();
    } catch (e) {
      setError(e?.message || 'Delete failed');
    }
  };

  const toggleActive = async (s) => {
    try {
      await apiClient.request(`/api/rebecca-manager/scripts/${s.script_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !s.active }),
      });
      await reload();
    } catch (e) { setError(e?.message || 'Toggle failed'); }
  };

  return (
    <div className="space-y-3" data-testid="rebecca-scripts-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Pre-authored messages Rebecca delivers verbatim. Multiple scripts on the same trigger fire in <code className="font-mono">order_index</code> ascending.
        </p>
        <div className="flex gap-2">
          <button onClick={reload} className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600" title="Reload" data-testid="rebecca-scripts-reload">
            <RefreshCcw size={14} />
          </button>
          <button
            onClick={() => setPreviewOpen(true)}
            className="px-3 py-1.5 text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md inline-flex items-center gap-1.5"
            data-testid="rebecca-scripts-preview-as-teen"
            title="Watch your scripted Rebecca sequence as a teen would experience it"
          >
            <PlayCircle size={14} /> Preview as a teen
          </button>
          <button onClick={startNew} className="px-3 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md inline-flex items-center gap-1.5" data-testid="rebecca-scripts-new-btn">
            <Plus size={14} /> New script
          </button>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle size={14} /> {error}</div>}

      {editingId && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-3" data-testid="rebecca-scripts-editor">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-gray-900">{editingId === 'new' ? 'New script' : 'Edit script'}</h4>
            <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-200 text-gray-500"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Title (admin label)</label>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                placeholder="Dashboard tour intro"
                data-testid="rebecca-script-title-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Trigger</label>
              <select
                value={draft.trigger_event}
                onChange={(e) => setDraft({ ...draft, trigger_event: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                data-testid="rebecca-script-trigger-input"
              >
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Rebecca says (verbatim)</label>
            <textarea
              value={draft.message}
              onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              rows={5}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40 font-serif leading-relaxed"
              placeholder="Hello, I would like to introduce you to your private Dashboard…"
              data-testid="rebecca-script-message-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Order index</label>
              <input
                type="number"
                value={draft.order_index ?? 0}
                onChange={(e) => setDraft({ ...draft, order_index: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                data-testid="rebecca-script-order-input"
              />
            </div>
            <label className="flex items-end gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.active !== false}
                onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                className="rounded text-purple-600 focus:ring-purple-500"
                data-testid="rebecca-script-active-input"
              />
              <span>Active</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={saveDraft} className="px-4 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md inline-flex items-center gap-1.5" data-testid="rebecca-script-save-btn">
              <Save size={13} /> Save
            </button>
            <button onClick={cancelEdit} className="px-4 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : scripts.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6" data-testid="rebecca-scripts-empty">
          No Rebecca scripts yet. Click &quot;New script&quot; to author the first one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3 font-semibold">Trigger</th>
                <th className="py-2 pr-3 font-semibold">Order</th>
                <th className="py-2 pr-3 font-semibold">Title</th>
                <th className="py-2 pr-3 font-semibold">Message</th>
                <th className="py-2 pr-3 font-semibold">Active</th>
                <th className="py-2 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map((s) => (
                <tr key={s.script_id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`maya-script-row-${s.script_id}`}>
                  <td className="py-2.5 pr-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold">
                      {TRIGGER_LABELS[s.trigger_event] || s.trigger_event}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-gray-500">{s.order_index}</td>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{s.title}</td>
                  <td className="py-2.5 pr-3 text-gray-600 max-w-md truncate" title={s.message}>{s.message}</td>
                  <td className="py-2.5 pr-3">
                    <button onClick={() => toggleActive(s)} className="text-purple-600" data-testid={`maya-script-toggle-${s.script_id}`}>
                      {s.active ? <ToggleRight size={20} /> : <ToggleLeft size={20} className="text-gray-400" />}
                    </button>
                  </td>
                  <td className="py-2.5 flex gap-1">
                    <button onClick={() => startEdit(s)} className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200" title="Edit" data-testid={`maya-script-edit-${s.script_id}`}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => removeScript(s.script_id)} className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200" title="Delete" data-testid={`maya-script-delete-${s.script_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RebeccaPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  );
};

// ── Teen AvA Templates panel (Sprint 207W) ─────────────────────────────
// Mirrors the ScriptsPanel layout but writes to `ava_conversation_templates`.
// These templates are delivered VERBATIM (bypassing the LLM) on a teen's
// first chat session with any non-Rebecca AvA, so the founder-authored
// onboarding pitch is never paraphrased or hallucinated.

const TemplatesPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await apiClient.request('/api/ava-templates/templates');
      setRows(data.rows || []);
    } catch (e) {
      setError(e?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const startNew = () => {
    setEditingId('new');
    setDraft({
      title: '',
      trigger: 'first_session',
      message_template: '',
      order_index: rows.filter(r => r.trigger === 'first_session').length,
      is_active: true,
    });
  };

  const startEdit = (r) => {
    setEditingId(r.template_id);
    setDraft({ ...r });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveDraft = async () => {
    setError(null);
    try {
      const body = {
        title: draft.title,
        trigger: draft.trigger,
        message_template: draft.message_template,
        order_index: Number(draft.order_index) || 0,
        is_active: draft.is_active !== false,
      };
      if (editingId === 'new') {
        await apiClient.request('/api/ava-templates/templates', {
          method: 'POST', body: JSON.stringify(body),
        });
      } else {
        await apiClient.request(`/api/ava-templates/templates/${editingId}`, {
          method: 'PATCH', body: JSON.stringify(body),
        });
      }
      cancelEdit();
      await reload();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const removeRow = async (template_id) => {
    if (!window.confirm('Delete this teen AvA template?')) return;
    try {
      await apiClient.request(`/api/ava-templates/templates/${template_id}`, { method: 'DELETE' });
      await reload();
    } catch (e) { setError(e?.message || 'Delete failed'); }
  };

  const toggleActive = async (r) => {
    try {
      await apiClient.request(`/api/ava-templates/templates/${r.template_id}`, {
        method: 'PATCH', body: JSON.stringify({ is_active: !r.is_active }),
      });
      await reload();
    } catch (e) { setError(e?.message || 'Toggle failed'); }
  };

  return (
    <div className="space-y-3" data-testid="ava-templates-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Verbatim onboarding messages delivered by <strong>non-Rebecca teen AvAs</strong> on first session. Use <code className="font-mono">{'{teen_name}'}</code> and <code className="font-mono">{'{ava_name}'}</code> for personalisation. Multiple templates on the same trigger fire in <code className="font-mono">order_index</code> ascending.
        </p>
        <div className="flex gap-2">
          <button onClick={reload} className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-600" title="Reload" data-testid="ava-templates-reload">
            <RefreshCcw size={14} />
          </button>
          <button
            onClick={() => setPreviewOpen(true)}
            className="px-3 py-1.5 text-xs font-semibold bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-md inline-flex items-center gap-1.5"
            data-testid="ava-templates-preview-as-teen"
            title="Preview the verbatim onboarding script a teen will see on first chat"
          >
            <PlayCircle size={14} /> Preview as a teen
          </button>
          <button onClick={startNew} className="px-3 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md inline-flex items-center gap-1.5" data-testid="ava-templates-new-btn">
            <Plus size={14} /> New template
          </button>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 text-sm text-red-600"><AlertCircle size={14} /> {error}</div>}

      {editingId && (
        <div className="rounded-lg border border-purple-200 bg-purple-50/40 p-4 space-y-3" data-testid="ava-templates-editor">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm text-gray-900">{editingId === 'new' ? 'New template' : 'Edit template'}</h4>
            <button onClick={cancelEdit} className="p-1 rounded hover:bg-gray-200 text-gray-500"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Title (admin label)</label>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                placeholder="First-session onboarding pitch"
                data-testid="ava-template-title-input"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Trigger</label>
              <select
                value={draft.trigger}
                onChange={(e) => setDraft({ ...draft, trigger: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                data-testid="ava-template-trigger-input"
              >
                {Object.entries(TRIGGER_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-gray-600 mb-1">Message template (delivered verbatim)</label>
            <textarea
              value={draft.message_template}
              onChange={(e) => setDraft({ ...draft, message_template: e.target.value })}
              rows={6}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40 font-serif leading-relaxed"
              placeholder="Hi {teen_name}! I'm {ava_name}, your practice partner…"
              data-testid="ava-template-message-input"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-1">Order index</label>
              <input
                type="number"
                value={draft.order_index ?? 0}
                onChange={(e) => setDraft({ ...draft, order_index: e.target.value })}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/40"
                data-testid="ava-template-order-input"
              />
            </div>
            <label className="flex items-end gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={draft.is_active !== false}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                className="rounded text-purple-600 focus:ring-purple-500"
                data-testid="ava-template-active-input"
              />
              <span>Active</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={saveDraft} className="px-4 py-1.5 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white rounded-md inline-flex items-center gap-1.5" data-testid="ava-template-save-btn">
              <Save size={13} /> Save
            </button>
            <button onClick={cancelEdit} className="px-4 py-1.5 text-xs font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-400 text-center py-6" data-testid="ava-templates-empty">
          No teen AvA templates yet. Click &quot;New template&quot; to author the first one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
                <th className="py-2 pr-3 font-semibold">Trigger</th>
                <th className="py-2 pr-3 font-semibold">Order</th>
                <th className="py-2 pr-3 font-semibold">Title</th>
                <th className="py-2 pr-3 font-semibold">Message</th>
                <th className="py-2 pr-3 font-semibold">Active</th>
                <th className="py-2 font-semibold w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.template_id} className="border-b border-gray-50 hover:bg-gray-50" data-testid={`ava-template-row-${r.template_id}`}>
                  <td className="py-2.5 pr-3 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-[10px] font-semibold">
                      {TRIGGER_LABELS[r.trigger] || r.trigger}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-xs text-gray-500">{r.order_index}</td>
                  <td className="py-2.5 pr-3 font-medium text-gray-900">{r.title}</td>
                  <td className="py-2.5 pr-3 text-gray-600 max-w-md truncate" title={r.message_template}>{r.message_template}</td>
                  <td className="py-2.5 pr-3">
                    <button onClick={() => toggleActive(r)} className="text-purple-600" data-testid={`ava-template-toggle-${r.template_id}`}>
                      {r.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} className="text-gray-400" />}
                    </button>
                  </td>
                  <td className="py-2.5 flex gap-1">
                    <button onClick={() => startEdit(r)} className="p-1.5 rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200" title="Edit" data-testid={`ava-template-edit-${r.template_id}`}>
                      <Pencil size={12} />
                    </button>
                    <button onClick={() => removeRow(r.template_id)} className="p-1.5 rounded-md bg-red-100 text-red-600 hover:bg-red-200" title="Delete" data-testid={`ava-template-delete-${r.template_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RebeccaPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} source="templates" />
    </div>
  );
};

export default RebeccaManagerTile;
